import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import linkifyHtml from 'linkify-html';
import { DateTime } from 'luxon';
import { EMPTY, finalize, Observable } from 'rxjs';
import { ConfirmationPopupComponent } from '../../libraries/popup/components/confirmation-popup/confirmation-popup.component';
import { SuccessPopupComponent } from '../../libraries/popup/components/success-popup/success-popup.component';
import { ICommentReplyModel, ICreatePostCommentModel, IPostCommentModel } from '../../models/post.model';
import { PostService } from '../../services/api/post/post.service';
import { CreatePostEventService } from '../../services/helper/create-post-event/create-post-event.service';
import { LoaderService } from '../../services/loader/loader.service';
import { StorageService } from '../../services/storage/storage.service';

@Component({
    selector: 'app-post-comment',
    standalone: false,
    templateUrl: './post-comment.component.html',
    styleUrl: './post-comment.component.scss',
})
export class PostCommentComponent implements OnInit {
    @Input() isTestimony = false;
    @Input() postId!: number;
    @Output() editCommentEvent = new EventEmitter<IPostCommentModel>();
    protected editingReplyId = signal<number | null>(null);
    protected getUserProfileDetails = this.storageService.getLoginUser();
    protected postIdSignal = signal<number>(0);
    protected postCommentSignal = signal<IPostCommentModel>({} as IPostCommentModel);
    protected coloredContent!: SafeHtml;
    protected isReplying = signal<boolean>(false);
    protected isEditingReply = signal<boolean>(false);
    protected isOwnComment = signal<boolean>(false);
    protected isOwnReply = signal<boolean>(false);
    protected visibleRepliesCount = signal(3);
    protected replyForm = this.fb.group({
        content: ['', Validators.required],
    });

    constructor(
        private createPostEventService: CreatePostEventService,
        private dialog: MatDialog,
        private fb: NonNullableFormBuilder,
        private loaderService: LoaderService,
        private postService: PostService,
        private sanitizer: DomSanitizer,
        private storageService: StorageService,
    ) {}

    get visibleReplies(): ICommentReplyModel[] {
        return this.postCommentSignal().responses?.slice(-this.visibleRepliesCount()) ?? [];
    }

    @Input() set postComment(value: IPostCommentModel) {
        this.postCommentSignal.set(value);
        this.isOwnComment.set(value.author.id === this.getUserProfileDetails?.id);
    }

    ngOnInit(): void {
        this.coloredContent = this.highlightHashtagsAndLinks(this.postCommentSignal().content);
    }

    protected editComment(): void {
        this.editCommentEvent.emit(this.postCommentSignal());
    }

    protected editReply(id: number): void {
        const item = this.visibleReplies.find((r) => r.id === id);
        if (!item) {
            return;
        }
        this.isEditingReply.set(true);
        this.isReplying.set(false);
        this.editingReplyId.set(id);
        this.replyForm.patchValue({ content: item.content });
    }

    protected openDeletePostPopup(id: number, isReply: boolean = false): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: `Delete ${isReply ? 'Reply' : 'Comment'}?`,
            message: `Are you sure you want to remove this ${isReply ? 'reply' : 'comment'}?`,
            iconPath: '/assets/images/shared/delete-red-icon.svg',
            confirmButtonLabel: 'Yes, Delete',
        };

        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (!result) {
                return;
            } else {
                this.postService
                    .deleteComment(id)
                    .pipe(finalize(() => this.loaderService.hide()))
                    .subscribe({
                        next: () => {
                            this.createPostEventService.notifyPostCreated();
                            const popupConfig = SuccessPopupComponent.getDialogConfig();
                            popupConfig.data = {
                                title: `${isReply ? 'Reply' : 'Comment'} Removed`,
                                message: `You have successfully deleted this ${isReply ? 'reply' : 'comment'}.`,
                                iconPath: '/assets/images/shared/success-green-icon.svg',
                            };
                            this.dialog.open(SuccessPopupComponent, popupConfig);
                        },
                        error: (error) => {
                            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                            errorPopupConfig.data = {
                                title: `Error`,
                                message: error.error?.message,
                            };
                            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                            return EMPTY;
                        },
                    });
            }
        });
    }

    protected toggleReply(): void {
        this.isReplying.update((prev) => !prev);
        this.replyForm.reset();
    }

    protected loadMoreReplies(): void {
        const total = this.postCommentSignal().responses?.length ?? 0;
        this.visibleRepliesCount.set(Math.min(total, this.visibleRepliesCount() + 3));
    }

    protected timeAgo(time: string): string | null {
        const now = DateTime.now();
        const postTime = DateTime.fromISO(time);
        const minutes = now.diff(postTime, 'minutes').minutes;
        const hours = now.diff(postTime, 'hours').hours;
        const days = now.diff(postTime, 'days').days;

        if (minutes < 1) {
            return 'Just now';
        }
        if (minutes < 60) {
            return `${Math.floor(minutes)}m ago`;
        }
        if (hours < 24) {
            return `${Math.floor(hours)}h ago`;
        }
        return `${Math.floor(days)}d ago`;
    }

    protected getVerseReference(): string {
        if (!this.postCommentSignal().verses) {
            return '';
        }

        const first = this.postCommentSignal().verses[0];
        const last = this.postCommentSignal().verses[this.postCommentSignal().verses.length - 1];

        const bookType = first.type;
        const chapter = first.chapter;

        // Handle single verse or a range
        const versePart = this.postCommentSignal().verses.length === 1 ? `${first.list}` : `${first.list}-${last.list}`;

        return `${bookType} ${chapter}:${versePart}`;
    }

    protected toggleLike(comment: IPostCommentModel): void {
        if (comment.liked) {
            this.postService.unlikePostComment(comment.id).subscribe({
                next: () => {
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        liked: false,
                        likeCount: Math.max(0, p.likeCount - 1),
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Unlike failed', error);
                },
            });
        } else {
            this.postService.likePostComment(comment.id).subscribe({
                next: () => {
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        liked: true,
                        likeCount: p.likeCount + 1,
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Like failed', error);
                },
            });
        }
    }

    protected toggleReplyLike(reply: ICommentReplyModel): void {
        if (reply.liked) {
            this.postService.unlikePostComment(reply.id).subscribe({
                next: () => {
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        responses: p.responses?.map((r) =>
                            r.id === reply.id ? { ...r, liked: false, likeCount: Math.max(0, r.likeCount - 1) } : r,
                        ),
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Unlike reply failed', error);
                },
            });
        } else {
            this.postService.likePostComment(reply.id).subscribe({
                next: () => {
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        responses: p.responses?.map((r) =>
                            r.id === reply.id ? { ...r, liked: true, likeCount: r.likeCount + 1 } : r,
                        ),
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Like reply failed', error);
                },
            });
        }
    }

    protected submitReply(): void {
        if (this.replyForm.invalid) {
            return;
        }

        const payload = {
            content: this.replyForm.controls.content.value,
            comment: this.postCommentSignal().id,
        };

        let obs$: Observable<IPostCommentModel>;

        if (this.isEditingReply()) {
            obs$ = this.postService.editComment(this.editingReplyId()!, payload as ICreatePostCommentModel);
        } else {
            obs$ = this.postService.createPostComment(this.postId, payload as ICreatePostCommentModel);
        }

        this.loaderService.show();
        obs$.pipe(finalize(() => this.loaderService.hide())).subscribe({
            next: (response) => {
                if (this.isEditingReply()) {
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        responses: p.responses?.map((r) =>
                            r.id === this.editingReplyId() ? { ...r, content: response.content } : r,
                        ),
                    }));
                    this.isEditingReply.set(false);
                    this.editingReplyId.set(null);
                } else {
                    this.createPostEventService.notifyPostCommentCreated();
                    this.postCommentSignal.update((p) => ({
                        ...p,
                        responses: [...(p.responses ?? []), response],
                        replyCount: (p.replyCount ?? 0) + 1,
                    }));
                }
                this.replyForm.reset();
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error submitting reply:', error);
                return EMPTY;
            },
        });
    }

    private highlightHashtagsAndLinks(text: string): SafeHtml {
        if (!text) {
            return '';
        }

        // Escape unsafe HTML just in case
        let formatted = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Replace newlines with <br>
        formatted = formatted.replace(/\n/g, '<br>');

        // Highlight hashtags (make them blue)
        formatted = formatted.replace(/(#\w+)/g, `<span style="color: #4F66FF;">$1</span>`);

        // Use linkify to make URLs clickable
        formatted = linkifyHtml(formatted, {
            target: '_blank',
            rel: 'noopener noreferrer',
            className: 'text-[#4F66FF] underline',
        });

        // Mark as safe HTML for Angular
        return this.sanitizer.bypassSecurityTrustHtml(formatted);
    }
}
